/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Button, Container, Link, Paragraph, Dialog, CodeSnippet } from '@waca/ca-ui-toolkit';
import unknownWarning128 from '@ba-ui-toolkit/ba-graphics/dist/illustrations-js/unknown-warning_128.js';
import stringResource from 'ba-common-nls';
import * as glassContext from 'ba-glass-context';
import TextIcon from '../components/TextIcon';
import './ErrorView.scss';

class ErrorDialogPreview extends Component<any, any> {
	static propTypes = {
		onClose: PropTypes.func,
	};

	render() {
		const { onClose, ...other } = this.props;

		return (
			<Dialog onClose={() => onClose()}>
				<Dialog.Header>{stringResource.get('reactErrorTrace')}</Dialog.Header>
				<Dialog.Body>
					<ErrorDetails {...other} />
				</Dialog.Body>
				<Dialog.Footer>
					<Dialog.Button
						primary
						label={stringResource.get('ok')}
						onClick={() => onClose()}
					/>
				</Dialog.Footer>
			</Dialog>
		);
	}
}

class ErrorDetails extends Component<any, any> {
	static propTypes = {
		error: PropTypes.any,
		errorInfo: PropTypes.any,
	};

	render() {
		const { error, errorInfo } = this.props;

		const value = `${error.stack}\n\n React Render Trace: ${errorInfo.componentStack}`;

		return <CodeSnippet value={value} style={{ height: '400px', overflow: 'auto' }} />;
	}
}

class ErrorView extends Component<any, any> {
	static propTypes = {
		headerText: PropTypes.string,
		bodyText: PropTypes.string,
		tryAgain: PropTypes.func,
	};
	state = {
		showDetails: false,
	};

	render() {
		const { showDetails } = this.state;
		const { bodyText, headerText, tryAgain } = this.props;

		return (
			<>
				<TextIcon className="mui-errorView">
					<TextIcon.Icon width="128" height="128" icon={unknownWarning128} />
					<TextIcon.Header>
						{headerText || stringResource.get('reactErrorHeader')}
					</TextIcon.Header>
					<TextIcon.Body>
						<Paragraph style={{ textAlign: 'center' }}>
							{bodyText || stringResource.get('reactError')}
						</Paragraph>
						<Link
							onClick={() => this.setState({ showDetails: !this.state.showDetails })}
						>
							{stringResource.get('reactErrorDetails')}
						</Link>
						{tryAgain && (
							<Container gutter={[3]}>
								<Button
									intent="primary"
									data-tid="tryAgainButton"
									label={stringResource.get('tryAgain')}
									title={stringResource.get('tryAgain')}
									aria-label={stringResource.get('tryAgain')}
									onClick={this.props.tryAgain}
								/>
							</Container>
						)}
					</TextIcon.Body>
				</TextIcon>
				{showDetails && (
					<ErrorDialogPreview
						{...this.props}
						onClose={() => this.setState({ showDetails: false })}
					/>
				)}
			</>
		);
	}
}

class ErrorToast extends Component<any, any> {
	state = {
		showDetails: false,
	};

	componentDidMount() {
		glassContext.showToast(stringResource.get('reactError'), {
			type: 'error',
			secondaryCallback: () => {
				this.setState({ showDetails: true });
			},
		});
	}

	render() {
		return this.state.showDetails ? (
			<ErrorDialogPreview
				{...this.props}
				onClose={() => this.setState({ showDetails: false })}
			/>
		) : null;
	}
}

export default function withErrorHandling({
	useToast,
	onCatch,
	errorProps,
}: {
	useToast?: boolean;
	onCatch?: (any, error: string, errorInfo: { componentStack: string }) => void;
	errorProps?: (any) => any;
} = {}) {
	return function(Comp) {
		return class ErrorHandler extends Component<any, any> {
			state = {
				error: null,
			};

			componentWillReceiveProps() {
				if (this._caught) {
					this.setState({ error: null });
					this._caught = false;
				}
			}

			componentDidCatch(error, errorInfo) {
				this.setState({ error: { error, errorInfo } }, () => (this._caught = true));
				onCatch && onCatch(this.props, error, errorInfo);
			}

			_caught: boolean;

			render() {
				const { tryAgain, ...other } = (errorProps && errorProps(this.props)) || {};
				const doTryAgain = () => {
					tryAgain();
					this.setState({ error: null });
				};

				if (this.state.error) {
					if (useToast) {
						return (
							<ErrorToast
								{...this.state.error}
								onClose={() => this.setState({ error: null })}
							/>
						);
					}

					return (
						<ErrorView
							{...this.state.error}
							{...other}
							tryAgain={tryAgain && doTryAgain}
						/>
					);
				}

				return <Comp {...this.props} />;
			}
		};
	};
}
